<template>
  <div class="cashback-page">
    <div class="bg-decoration"></div>
    <div class="bg-decoration-2"></div>

    <div class="main-content">
      <!-- 未通过签名验证引导 -->
      <div v-if="!authVerified" class="no-access">
        <div v-if="authVerifying" class="no-access-card">
          <i class="el-icon-loading no-access-icon"></i>
          <p class="no-access-title">正在验证身份...</p>
        </div>
        <div v-else-if="authDeniedReason" class="no-access-card denied">
          <i class="el-icon-warning no-access-icon"></i>
          <p class="no-access-title">无法参与活动</p>
          <p class="no-access-desc">{{ authDeniedReason }}</p>
        </div>
        <div v-else class="no-access-card">
          <i class="el-icon-warning-outline no-access-icon"></i>
          <p class="no-access-title">请通过App内活动链接进入</p>
          <p class="no-access-desc">本活动页需在陌撩漂流瓶App内打开，方能识别您的身份并参与奖励活动。</p>
        </div>
      </div>

      <template v-else>
        <!-- 顶部返回 -->
        <div class="top-bar">
          <span class="back-btn" @click="goBack"><i class="el-icon-arrow-left"></i> 活动中心</span>
          <span class="brand-name">陌撩漂流瓶</span>
        </div>

        <!-- 主视觉 Hero -->
        <div class="hero-card">
          <div class="hero-badge"><span class="dot"></span>限定活动</div>
          <h1 class="hero-title">限定活动·热力开启</h1>
          <p class="hero-sub">提现达标最高返20%奖励，每周循环开启</p>

          <div class="period-box">
            <div class="period-row">
              <span class="period-label">当期活动</span>
              <span class="period-value">{{ periodText }}</span>
            </div>
            <div class="period-row">
              <span class="period-label">距结束</span>
              <span class="period-value countdown">{{ countdown }}</span>
            </div>
          </div>
        </div>

        <!-- 温馨提示 -->
        <div class="tip-banner">
          <i class="el-icon-bell"></i>
          <span>如需快速起步点击「<b class="guide-link" @click="showGuide = true">流量提升指南</b>」</span>
        </div>

        <!-- 流量提升指南弹框 -->
        <el-dialog
          title=""
          :visible.sync="showGuide"
          width="88%"
          :append-to-body="true"
          :close-on-click-modal="true"
          custom-class="guide-dialog"
          @close="showGuide = false"
        >
          <div class="guide-content">
            <div class="guide-header">
              <i class="el-icon-reading"></i>
              <span>流量提升指南</span>
            </div>
            <ol class="guide-list">
              <li>保持 APP 前台在线、完成实名与真人认证，享优先推送。</li>
              <li>新男性用户自动发布一条动态，助力快速起步。</li>
              <li>发布优质动态及音域内容，可有效提升账号曝光量。</li>
              <li>漂流瓶无需重复投放，保持在线即可获得优先投放权重。</li>
            </ol>
            <div class="guide-warm-tip">
              <i class="el-icon-warning-outline"></i>
              <p>
                温馨提示：仅停留在 APP 内为有效在线；<br>
                切换至后台或关闭 APP 将视为离线。如有疑问，请联系客服。
              </p>
            </div>
          </div>
        </el-dialog>

        <!-- 我的奖励进度 -->
        <div class="progress-card">
          <div class="card-title">
            <span>我的奖励进度</span>
          </div>

          <div class="stat-row">
            <div class="stat-block">
              <div class="stat-num coin">{{ formatMoney(status.cumulative_money) }}</div>
              <div class="stat-label">累计提现（元）</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-block">
              <div class="stat-num">{{ formatMoney(status.estimated_cashback) }}</div>
              <div class="stat-label">预计奖励（元）</div>
            </div>
          </div>

          <div class="progress-bar">
            <div class="bar-track">
              <div class="bar-fill" :style="{ width: progressPercent + '%' }"></div>
            </div>
            <div class="bar-info">
              <span>{{ formatCoin(status.cumulative_coin) }} / {{ formatCoin(status.threshold) }}</span>
              <span class="rate">奖励比例 {{ Math.round((status.rate || 0) * 100) }}%</span>
            </div>
          </div>

          <div class="threshold-tip">
            <i class="el-icon-info"></i>
            <span v-if="status.rate === 0.2">
              累计提现满 <b>10,000</b> 陌撩币，返现 <b>20%</b> 至支付宝
            </span>
            <span v-else-if="status.rate === 0.1">
              累计提现满 <b>5,000</b> 陌撩币，返现 <b>10%</b> 至支付宝；满 10,000 可享 20%
            </span>
            <span v-else>
              累计提现满 <b>5,000</b> 陌撩币可享 10% 返现；<br>
              满 <b>10,000</b> 陌撩币可享 20% 返现；未达标无返现。
            </span>
          </div>

          <div class="refresh-row">
            <span class="refresh-btn" @click="fetchStatus"><i class="el-icon-refresh"></i> 刷新数据</span>
          </div>
        </div>

        <!-- 奖励规则 -->
        <div class="rules-card">
          <div class="card-title"><i class="el-icon-document-checked"></i><span>奖励规则</span></div>
          <ul class="rule-list">
            <li>
              <div class="rule-head"><span class="rule-no">1</span>阶梯返现</div>
              <p class="rule-desc">活动期间账户，累计提现低于 <b>5,000</b> 陌撩币没有奖励。
                <br>累计提现满 <b>5,000</b> 陌撩币，返现 <b>10%</b> 至支付宝账户；
                <br>累计提现满 <b>10,000</b> 陌撩币，返现 <b>20%</b> 至支付宝账户；
                </p>
            </li>
            <li>
              <div class="rule-head"><span class="rule-no">2</span>计算示例</div>
              <p class="rule-desc">累计提现 10,000 陌撩币，实际提现 67.5 元，返现 <b>13.5 元</b> 至支付宝账户；
                <br>累计提现 5,000 陌撩币，实际提现 29.7 元，返现 <b>2.97 元</b> 至支付宝账户。</p>
            </li>
            <li>
              <div class="rule-head"><span class="rule-no">3</span>累计范围</div>
              <p class="rule-desc">仅统计活动期间内的提现记录，活动开始前的提现记录及其他活动奖励 <b>不计入</b> 本次累计。</p>
            </li>
            <li>
              <div class="rule-head"><span class="rule-no">4</span>发放时间</div>
              <p class="rule-desc">当期活动结束后 <b>次日</b>，奖励统一发放到用户支付宝账户。</p>
            </li>
            <li>
              <div class="rule-head"><span class="rule-no">5</span>发放方式</div>
              <p class="rule-desc">奖励将直接转账至最近一次成功提现的用户 <b>支付宝账户</b>，无需手动领取，请确保支付宝账户信息正确。</p>
            </li>
          </ul>
        </div>

        <!-- 违规处理 -->
        <div class="violation-card">
          <div class="card-title"><i class="el-icon-warning"></i><span>违规处理</span></div>
          <div class="violation-block">
            <div class="v-label">禁止行为</div>
            <p class="v-text">使用脚本、招嫖、诈骗、辱骂、发布第三方平台信息或账号等违规行为。</p>
          </div>
          <div class="violation-block">
            <div class="v-label">处理措施</div>
            <p class="v-text">一经发现将 <b>取消本次与后续活动资格</b> 并 <b>封禁账号</b>。</p>
          </div>
        </div>

        <!-- 奖励记录 -->
        <div class="records-card">
          <div class="card-title"><i class="el-icon-tickets"></i><span>奖励记录</span></div>
          <div v-loading="recordsLoading" class="list-wrap">
            <div v-if="records.length" class="record-list">
              <div v-for="item in records" :key="item.id" class="record-item">
                <div class="record-left">
                  <div class="record-period">{{ item.period_text }}</div>
                  <div class="record-meta">返现金额 {{ formatMoney(item.cashback_amount) }} 元</div>
                  <div v-if="item.created_at" class="record-time">
                    <i class="el-icon-time"></i>
                    <span>到账时间 {{ formatCreatedAt(item.created_at) }}</span>
                  </div>
                </div>
                <div class="record-right">
                  <el-tag size="mini" :type="recordStatusType(item.status)" effect="plain">{{ recordStatusText(item.status) }}</el-tag>
                </div>
              </div>
            </div>
            <el-empty v-else-if="!recordsLoading" description="暂无奖励记录" :image-size="90"></el-empty>
          </div>
          <div v-if="recordsTotal > recordsPageSize" class="pager">
            <el-pagination
              background
              layout="prev, pager, next"
              :current-page="recordsPage"
              :page-size="recordsPageSize"
              :total="recordsTotal"
              small
              @current-change="onPageChange"
            ></el-pagination>
          </div>
        </div>

        <div class="bottom-bar">© 2026 陌撩漂流瓶</div>
      </template>
    </div>
  </div>
</template>

<script>
import { CashbackStatusApi, CashbackRecordsApi, VerifySignatureApi } from '@/api/api.js'
import { setToken } from '@/utils/dataStorage.js'
import { setAuthParams, buildAuthQuery, isAuthParamsComplete } from '@/utils/authParams.js'

export default {
  name: 'CashbackActivity',
  data() {
    return {
      userId: '',
      authVerified: false,
      authVerifying: true,
      authDeniedReason: '',
      token: '',
      now: Date.now(),
      timer: null,
      status: {
        is_newcomer: false,
        threshold: 5000,
        rate: 0.0,
        cumulative_coin: 0,
        cumulative_money: 0.0,
        estimated_cashback: 0.0
      },
      records: [],
      recordsTotal: 0,
      recordsPage: 1,
      recordsPageSize: 10,
      recordsLoading: false,
      showGuide: false
    }
  },
  computed: {
    // 当期活动周期：每周五 00:00 至下周四 24:00（= 下个周五 00:00 前）
    period() {
      const ref = new Date(this.now);
      const day = ref.getDay(); // 0=Sun, 5=Fri, 6=Sat
      const daysSinceFri = (day - 5 + 7) % 7;
      const start = new Date(ref);
      start.setHours(0, 0, 0, 0);
      start.setDate(start.getDate() - daysSinceFri);
      const end = new Date(start);
      end.setDate(start.getDate() + 7); // 下周五 00:00（不含）= 周四 24:00
      return { start, end };
    },
    periodText() {
      return this.fmtDate(this.period.start) + ' - ' + this.fmtDate(new Date(this.period.end.getTime() - 1));
    },
    countdown() {
      const diff = this.period.end.getTime() - this.now;
      if (diff <= 0) return '已结束';
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      return d + '天 ' + h + '时 ' + m + '分';
    },
    // 进度百分比：基于累计陌撩币与最低门槛(5000)的比例
    progressPercent() {
      const cumulative = Number(this.status.cumulative_coin || 0);
      const threshold = Number(this.status.threshold || 5000);
      if (threshold <= 0) return 0;
      return Math.min(100, Math.round(cumulative / threshold * 100));
    }
  },
  mounted() {
    const query = this.$route.query || {};
    if (query.token) {
      this.token = query.token;
      setToken(query.token);
    }
    // 参数完整性检查：签名参数齐全才请求后端验证签名
    if (isAuthParamsComplete(query)) {
      setAuthParams(query);
      VerifySignatureApi().then(res => {
        if (res && res.code === 200 && res.data && res.data.user_id) {
          this.userId = String(res.data.user_id);
          this.authVerified = true;
          this.fetchStatus();
          this.fetchRecords();
        } else {
          this.authVerified = false;
        }
      }).catch(err => {
        // 提取 403 业务限制原因（性别限制/黑名单等），显示友好的限制提示卡片
        const detail = err && err.response && err.response.data && err.response.data.detail;
        if (detail) {
          this.authDeniedReason = detail;
        }
        this.authVerified = false;
      }).finally(() => {
        this.authVerifying = false;
      });
    } else {
      // 参数不全，直接拒绝
      this.authVerified = false;
      this.authVerifying = false;
    }
    // 倒计时每分钟刷新
    this.timer = setInterval(() => { this.now = Date.now(); }, 60000);
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
  },
  methods: {
    goBack() {
      this.$router.push({ path: '/activity/list', query: buildAuthQuery(this.$route.query) });
    },
    fetchStatus() {
      // 从 period 计算属性提取日期范围传递给后端
      const startDate = this.formatApiDate(this.period.start);
      // 结束日期为 period.end - 1ms（即周四 23:59:59），取其日期部分
      const endDate = this.formatApiDate(new Date(this.period.end.getTime() - 1));
      CashbackStatusApi({ start_date: startDate, end_date: endDate }).then(res => {
        if (res && res.code === 200 && res.data) {
          this.status = Object.assign({
            is_newcomer: false,
            threshold: 5000,
            rate: 0.0,
            cumulative_coin: 0,
            cumulative_money: 0.0,
            estimated_cashback: 0.0
          }, res.data);
        }
      }).catch(() => {});
    },
    fetchRecords() {
      this.recordsLoading = true;
      CashbackRecordsApi({
        page: this.recordsPage,
        page_size: this.recordsPageSize
      }).then(res => {
        if (res && res.code === 200) {
          this.records = res.data || [];
          this.recordsTotal = res.count || 0;
        }
      }).catch(() => {}).finally(() => {
        this.recordsLoading = false;
      });
    },
    onPageChange(p) {
      this.recordsPage = p;
      this.fetchRecords();
    },
    recordStatusText(status) {
      if (status === 0) return '待提现';
      if (status === 1) return '已提现';
      if (status === 2) return '已驳回';
      if (status === 3) return '提现失败';
      return '未知';
    },
    recordStatusType(status) {
      if (status === 0) return 'warning';
      if (status === 1) return 'success';
      if (status === 2) return 'danger';
      return 'info';
    },
    // 格式化陌撩币（整数，千分位）
    formatCoin(n) {
      return Number(n || 0).toLocaleString('en-US');
    },
    // 格式化金额（RMB，保留2位小数，千分位）
    formatMoney(n) {
      return Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    fmtDate(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return y + '.' + m + '.' + day;
    },
    // 格式化日期为 YYYY-MM-DD，用于 API 参数传递
    formatApiDate(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return y + '-' + m + '-' + day;
    },
    // 格式化到账时间：将 "2026-07-13 10:30:00" 转为 "07-13 10:30" 更紧凑友好
    formatCreatedAt(str) {
      if (!str) return '';
      // 兼容 "YYYY-MM-DD HH:mm:ss" 和 "YYYY-MM-DD HH:mm" 格式
      const match = str.match(/^\d{4}-(\d{2})-(\d{2})\s+(\d{2}:\d{2})/);
      if (match) {
        return match[1] + '-' + match[2] + ' ' + match[3];
      }
      return str;
    }
  }
}
</script>

<style scoped>
.cashback-page {
  min-height: 100vh;
  background: linear-gradient(160deg, #fff7f0 0%, #fff5f9 40%, #fdf2f8 100%);
  position: relative;
  overflow: hidden;
}

.bg-decoration {
  position: absolute;
  top: -160px;
  right: -160px;
  width: 420px;
  height: 420px;
  background: radial-gradient(circle, rgba(255, 138, 61, 0.18) 0%, transparent 70%);
  border-radius: 50%;
  animation: float 6s ease-in-out infinite;
}

.bg-decoration-2 {
  position: absolute;
  bottom: -120px;
  left: -120px;
  width: 320px;
  height: 320px;
  background: radial-gradient(circle, rgba(255, 77, 141, 0.12) 0%, transparent 70%);
  border-radius: 50%;
  animation: float 8s ease-in-out infinite reverse;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(20px, 20px) scale(1.08); }
}

.main-content {
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  padding: 18px 18px 40px;
  position: relative;
  z-index: 1;
}

/* 顶部栏 */
.top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.back-btn {
  font-size: 14px;
  color: #ff4d8d;
  font-weight: 600;
  display: flex;
  align-items: center;
  cursor: pointer;
}
.back-btn i {
  margin-right: 4px;
}
.brand-name {
  font-size: 14px;
  font-weight: 700;
  background: linear-gradient(135deg, #ff8a3d 0%, #ff4d8d 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* 未授权 */
.no-access-card {
  background: #fff;
  border-radius: 24px;
  padding: 56px 28px;
  text-align: center;
  box-shadow: 0 12px 40px rgba(255, 138, 61, 0.12);
  margin-top: 80px;
}
.no-access-card.denied {
  border: 1px solid #fde2e2;
  background: linear-gradient(135deg, #fff5f5 0%, #fff8f2 100%);
}
.no-access-card.denied .no-access-icon {
  color: #f56c6c;
}
.no-access-icon {
  font-size: 52px;
  color: #ff8a3d;
  margin-bottom: 16px;
  display: block;
}
.no-access-title {
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin-bottom: 10px;
}
.no-access-desc {
  font-size: 14px;
  color: #999;
  line-height: 1.7;
}

/* Hero */
.hero-card {
  background: linear-gradient(135deg, #ff8a3d 0%, #ff4d8d 50%, #a855f7 100%);
  border-radius: 26px;
  padding: 26px 24px 22px;
  color: #fff;
  text-align: center;
  box-shadow: 0 16px 44px rgba(255, 77, 141, 0.35);
  position: relative;
  overflow: hidden;
  margin-bottom: 14px;
}
.hero-card::after {
  content: '';
  position: absolute;
  top: -40px;
  right: -40px;
  width: 160px;
  height: 160px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%);
  border-radius: 50%;
}
.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.22);
  padding: 5px 14px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 12px;
}
.hero-badge .dot {
  width: 6px;
  height: 6px;
  background: #fff;
  border-radius: 50%;
  animation: blink 1.4s ease-in-out infinite;
}
@keyframes blink {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}
.hero-title {
  font-size: 24px;
  font-weight: 800;
  margin-bottom: 6px;
  letter-spacing: 1px;
}
.hero-sub {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.92);
  margin-bottom: 18px;
}
.period-box {
  background: rgba(255, 255, 255, 0.16);
  border-radius: 16px;
  padding: 12px 16px;
}
.period-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  padding: 4px 0;
}
.period-label {
  color: rgba(255, 255, 255, 0.85);
}
.period-value {
  font-weight: 700;
}
.period-value.countdown {
  color: #fff;
  font-size: 15px;
}

/* 温馨提示 */
.tip-banner {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px dashed #ffb38a;
  border-radius: 14px;
  padding: 12px 14px;
  margin-bottom: 14px;
  font-size: 13px;
  color: #8a5a3a;
}
.tip-banner i {
  color: #ff8a3d;
  margin-right: 8px;
  font-size: 16px;
}
.tip-banner b {
  color: #ff4d8d;
}
.guide-link {
  color: #ff4d8d;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
}

/* 流量提升指南弹框 */
.guide-dialog {
  border-radius: 22px !important;
  overflow: hidden;
}
.guide-dialog .el-dialog__header {
  padding: 0;
}
.guide-dialog .el-dialog__body {
  padding: 0;
}
.guide-content {
  padding: 10px;
}
.guide-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 20px;
  font-weight: 800;
  color: #ff4d8d;
  margin-bottom: 22px;
}
.guide-header i {
  font-size: 24px;
  color: #ff8a3d;
}
.guide-list {
  list-style: none;
  counter-reset: guide-counter;
}
.guide-list li {
  counter-increment: guide-counter;
  position: relative;
  padding: 10px 12px 10px 42px;
  margin-bottom: 10px;
  font-size: 14px;
  color: #444;
  line-height: 1.7;
  background: linear-gradient(135deg, #fff8f2 0%, #fff5f9 100%);
  border-radius: 12px;
  border: 1px solid rgba(255, 138, 61, 0.1);
}
.guide-list li::before {
  content: counter(guide-counter);
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  background: linear-gradient(135deg, #ff8a3d 0%, #ff4d8d 100%);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.guide-warm-tip {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #fff3e0;
  border-radius: 12px;
  padding: 12px 14px;
  border: 1px dashed #ffb38a;
}
.guide-warm-tip i {
  color: #ff8a3d;
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 1px;
}
.guide-warm-tip p {
  margin: 0;
  font-size: 12px;
  color: #8a5a3a;
  line-height: 1.7;
}

/* 通用卡片 */
.progress-card,
.rules-card,
.violation-card,
.records-card {
  background: #fff;
  border-radius: 22px;
  padding: 18px 16px;
  box-shadow: 0 8px 28px rgba(255, 138, 61, 0.1);
  margin-bottom: 14px;
  border: 1px solid rgba(255, 138, 61, 0.06);
}
.card-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 700;
  color: #333;
  margin-bottom: 14px;
}
.card-title i {
  color: #ff8a3d;
  margin-right: 8px;
  font-size: 18px;
}

/* 进度卡 */
.newcomer-tag {
  margin-left: auto;
  background: linear-gradient(135deg, #ff8a3d 0%, #ff4d8d 100%);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 10px;
}
.stat-row {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}
.stat-block {
  flex: 1;
  text-align: center;
}
.stat-num {
  font-size: 24px;
  font-weight: 800;
  color: #ff4d8d;
  margin-bottom: 4px;
  line-height: 1.1;
}
.stat-num.coin {
  color: #ff8a3d;
}
.stat-label {
  font-size: 12px;
  color: #999;
}
.stat-divider {
  width: 1px;
  height: 36px;
  background: #f0e0d6;
}
.progress-bar {
  margin-bottom: 12px;
}
.bar-track {
  height: 10px;
  background: #fff0e6;
  border-radius: 6px;
  overflow: hidden;
}
.bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #ff8a3d 0%, #ff4d8d 100%);
  border-radius: 6px;
  transition: width 0.4s ease;
}
.bar-info {
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  font-size: 12px;
  color: #999;
}
.bar-info .rate {
  color: #ff4d8d;
  font-weight: 600;
}
.threshold-tip {
  display: flex;
  align-items: flex-start;
  font-size: 12px;
  color: #888;
  line-height: 1.6;
  background: #fff8f2;
  border-radius: 10px;
  padding: 10px 12px;
}
.threshold-tip i {
  color: #ff8a3d;
  margin-right: 6px;
  margin-top: 2px;
  flex-shrink: 0;
}
.threshold-tip b {
  color: #ff4d8d;
}
.refresh-row {
  text-align: center;
  margin-top: 12px;
}
.refresh-btn {
  font-size: 12px;
  color: #ff8a3d;
  cursor: pointer;
}
.refresh-btn i {
  margin-right: 4px;
}

/* 规则 */
.rule-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.rule-list li {
  margin-bottom: 14px;
}
.rule-list li:last-child {
  margin-bottom: 0;
}
.rule-head {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}
.rule-no {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #ff8a3d 0%, #ff4d8d 100%);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  border-radius: 50%;
  margin-right: 8px;
  flex-shrink: 0;
}
.rule-desc {
  font-size: 13px;
  color: #666;
  line-height: 1.7;
  margin: 0 0 0 28px;
}
.rule-desc b {
  color: #ff4d8d;
}

/* 违规 */
.violation-block {
  margin-bottom: 12px;
}
.violation-block:last-child {
  margin-bottom: 0;
}
.v-label {
  font-size: 13px;
  font-weight: 600;
  color: #e6443a;
  margin-bottom: 4px;
}
.v-text {
  font-size: 13px;
  color: #666;
  line-height: 1.7;
  margin: 0;
}
.v-text b {
  color: #e6443a;
}

/* 记录 */
.list-wrap {
  min-height: 100px;
}
.record-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px dashed #f0e0d6;
}
.record-item:last-child {
  border-bottom: none;
}
.record-period {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 3px;
}
.record-meta {
  font-size: 12px;
  color: #999;
  margin-bottom: 2px;
}
.record-time {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #b0a0a0;
  margin-top: 2px;
}
.record-time i {
  font-size: 12px;
  margin-right: 3px;
  color: #d4b896;
}
.record-right {
  flex-shrink: 0;
  margin-left: 10px;
  align-self: center;
}
.pager {
  margin-top: 12px;
  text-align: center;
}
.pager >>> .el-pagination.is-background .el-pager li:not(.disabled).active {
  background-color: #ff8a3d;
}
.pager >>> .el-pagination.is-background .btn-prev,
.pager >>> .el-pagination.is-background .btn-next,
.pager >>> .el-pagination.is-background .el-pager li {
  background-color: #fff;
}

.bottom-bar {
  text-align: center;
  font-size: 12px;
  color: #ccc;
  margin-top: 20px;
}

@media (min-width: 481px) {
  .main-content {
    padding: 28px 18px 48px;
  }
  .hero-title {
    font-size: 28px;
  }
}

@media (max-width: 360px) {
  .hero-title {
    font-size: 21px;
  }
  .stat-num {
    font-size: 20px;
  }
  .rule-desc,
  .v-text {
    font-size: 12px;
  }
  .record-time {
    font-size: 10px;
  }
}
</style>
